import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { hasMinRole, type SystemRole } from "@/lib/constants";
import { slugify } from "@/lib/slugify";
import type { DBDocumentFolder } from "@/lib/db-types";

/**
 * VIB-2703: mappene i dokumentarkivet.
 *
 * Lista er allerede filtrert av RLS — `document_folders_select` slipper kun
 * gjennom mapper kalleren kan lese. Vi filtrerer altså IKKE på rolle her i
 * tillegg: gjorde vi det, ville de to stedene kunne divergere, og den i
 * frontend er uansett den som ikke teller.
 */
export function useDocumentFolders() {
  return useQuery({
    queryKey: ["document-folders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_folders")
        .select("*")
        .is("deleted_at", null)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return (data ?? []) as DBDocumentFolder[];
    },
  });
}

/**
 * Om aktiv bruker kan skrive i mappa. Brukes KUN til å vise eller skjule
 * knapper — selve tilgangen håndheves av RLS og storage-policyene, og en
 * knapp som er borte er ikke en sikkerhetsmekanisme.
 */
export function canWriteFolder(
  folder: Pick<DBDocumentFolder, "write_min_role">,
  role: string | undefined,
): boolean {
  return hasMinRole(role ?? "user", folder.write_min_role as SystemRole);
}

/**
 * Slug fra et mappenavn, eventuelt prefikset med forelderens.
 *
 * Translittererer æ/ø/å i stedet for å strippe dem: uten det ville «Årsmøter»
 * og «Arsmoter» fått hver sin uleselige slug.
 */
export function buildFolderSlug(name: string, parentSlug?: string | null): string {
  // Normaliseringen er felles med tilgangsgruppene (VIB-2726). To kopier av
  // samme slug-regel kan begynne å drifte uten at noen merker det.
  const egen = slugify(name);
  if (!egen) return "";
  return parentSlug ? `${parentSlug}-${egen}` : egen;
}

export function useCreateDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      readMinRole: SystemRole;
      writeMinRole: SystemRole;
      createdById: string;
      /** VIB-2721: forelder når dette er en undermappe. */
      parentId?: string | null;
      /**
       * Forelderens slug. `slug` har UNIQUE på hele tabellen, så to undermapper
       * som heter «Arkiv» under hver sin forelder ville ellers kollidert og gitt
       * «det finnes allerede en mappe med dette navnet» for et navn som er
       * ledig der brukeren står. Prefikset gjør slugen unik der navnet er det.
       */
      parentSlug?: string | null;
    }) => {
      const slug = buildFolderSlug(input.name, input.parentSlug);
      if (!slug) throw new Error("Mappenavnet må inneholde minst én bokstav.");

      const { data, error } = await supabase
        .from("document_folders")
        .insert({
          name: input.name.trim(),
          slug,
          read_min_role: input.readMinRole,
          write_min_role: input.writeMinRole,
          parent_id: input.parentId ?? null,
          created_by: input.createdById,
        })
        .select()
        .single();
      if (error) {
        // UNIQUE på slug: to mapper som heter nesten det samme gir samme slug.
        if (error.code === "23505") {
          throw new Error("Det finnes allerede en mappe med dette navnet.");
        }
        throw error;
      }
      return data as DBDocumentFolder;
    },
    onSuccess: () => toast.success("Mappe opprettet"),
    onError: (e: Error) => toast.error(e.message),
    onSettled: async () => {
      await invalidateFor(qc, "document_folders");
    },
  });
}

export function useUpdateDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      readMinRole?: SystemRole;
      writeMinRole?: SystemRole;
      sortOrder?: number;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name.trim();
      if (input.readMinRole !== undefined) patch.read_min_role = input.readMinRole;
      if (input.writeMinRole !== undefined) patch.write_min_role = input.writeMinRole;
      if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;

      const { error } = await supabase
        .from("document_folders")
        .update(patch)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => toast.success("Mappe oppdatert"),
    onError: (e: Error) => toast.error(e.message),
    onSettled: async (_d, _e, vars) => {
      await invalidateFor(qc, "document_folders", { folderId: vars.id });
    },
  });
}

/**
 * Soft-delete. Systemmappene vernes av DB-triggeren
 * `protect_system_document_folders`, så feilmeldingen her kommer fra
 * databasen og ikke fra en sjekk vi kunne glemt å gjøre.
 *
 * Går via RPC av samme grunn som useDeleteDocument: en UPDATE av deleted_at
 * avvises av SELECT-policyen for alle roller.
 */
export function useDeleteDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase.rpc("soft_delete_document_folder", { p_id: input.id });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Mappe slettet"),
    onError: (e: Error) => toast.error(e.message),
    onSettled: async (_d, _e, vars) => {
      await invalidateFor(qc, "document_folders", { folderId: vars.id });
    },
  });
}
